'use strict';
import { Injectable } from '@angular/core';
import { Observable, of as observableOf } from 'rxjs';
import { Store } from '@ngrx/store';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { get, has, includes, isEmpty, map as lodashMap, omit, set } from 'lodash';

import { AgregationService, BundleCall } from '@ispm-client/modules/aggregator/services';
import { AttachmentService } from '@ispm/migration/services/attachment.service';
import { AuthenticationService } from '@ispm/user/services/authentication.service';
import { BUTTON_ACTIONS } from '../components/dashboard/constants/constants';
import { CORE_ACTIONS } from '@ispm/core/constants/core-actions.constant';
import { CURRENT_ORDER_ACTIONS } from '@ispm/dashboard/test/constants/current-order.constants';
import { ConfirmModalComponent } from '@ispm/layout/components/modals/confirm-modal/confirm-modal.component';
import { ConfirmModalLoaderPayload } from '@ispm/layout/classes/confirm-modal-loader-payload';
import { ActivatedRoute, Router } from '@angular/router';
import { FormHandlerService } from '@ispm/dynamic-form/services/form-handler.service';
import { HttpInterceptorService } from '@ispm/core/services/http-interceptor.service';
import { ISelectedFacilityAssociation } from '@ispm-client/modules/aggregator/interfaces';
import { IgenomicsState } from '@ispm/core/interfaces/genomics-state.interface';
import { InterpolateService } from '@ispm/shared/services/interpolate.service';
import { LogService } from '@ispm/core/services/log.service';
import { ModalTypes } from '@ispm/layout/components/modals/contants/modal-types.enum';
import { NOTIFICATION_MESSAGE } from '@ispm-client/modules/aggregator/services/consts';
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';
import { PollJobService } from '@ispm/dashboard/test/services/poll-job.service';
import { RouterManagerService } from '@ispm/core/services/router-manager.service';
import { TableItemInterface, TABLELIST_ACTIONS } from '../interfaces';
import { TestService } from '@ispm/test/services/test.service';
import { TunnelResult } from '@ispm/layout/classes/tunnel-result';
import { modalResponse } from '@ispm/layout/constants/modal-responses.enum';
import {DiagnosticOrderService} from "@ispm/test/services/diagnostic-order.service";
import {DynamicModalLoaderService} from "@ispm/layout/services/dynamic-modal-loader.service";
import {Filter} from "@ispm-shared/modules/common";
import {DiagnosticOrder} from "@ispm/test/classes/DiagnosticOrder";
import {DIAGNOSTIC_ORDER_ACTIONS} from "@ispm/test/constants/diagnostic-order.constants";
import {WorklistHelper} from "../worklist-helper";

@Injectable()
export class DashboardService {
  public dashboardConfig: any;
  public newCaseTunnel: Observable<TunnelResult>;
  public newTestConfig: any;
  public stateParams: any = { };
  public transactionId: string;
  public workflowId: string;

  private channelName: string;
  private currentApp: any;
  private nonClinicalUsage: any;
  private readonly  DASHBOARD_SERVICE = 'DASHBOARD_SERVICE';
  private store$;

  constructor(
    private _attachmentService: AttachmentService,
    private _aggregationService: AgregationService,
    private _bundleCall: BundleCall,
    private _diagnosticOrderService: DiagnosticOrderService,
    private _dynamicModalLoader: DynamicModalLoaderService,
    private _formHandler: FormHandlerService,
    private _isgHttpClient: HttpInterceptorService,
    private _store: Store<IgenomicsState>,
    private authService: AuthenticationService,
    private interpolateService: InterpolateService,
    private logService: LogService,
    private notificationMessage: NOTIFICATION_MESSAGE,
    private notificationMessageService: NotificationMessageService,
    private pollJobService: PollJobService,
    private routerManagerService: RouterManagerService,
    private testService: TestService,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {
    this.store$ = _store.select('users') as any;
    _store.select('navigation', 'currentApp').subscribe((app) => {
      this.currentApp = app;
    });
    this.stateParams = this.routerManagerService.parameters;
    this.transactionId = this.stateParams.transactionId || logService.generateTransactionId();
  }

  /**
   *   Check if any fields defined in paramsToSet attribute in payload. If it is there, set the values for it
   *   using aggregator service. Also, Perform the action required(likewise open modal,routing etc.) as per payload command
   */
  public runConfiguration(testConfiguration, dashboardConfig?: any, patientId?: string, channelName?: ChannelTypes) {
    const configuration = JSON.parse(JSON.stringify(testConfiguration));
    this.newTestConfig = testConfiguration;
    this.dashboardConfig = dashboardConfig;

    if (!Array.isArray(configuration.payload.paramsToSet) && has(configuration.payload.paramsToSet, 'pathToField')) {
      configuration.payload.paramsToSet = Array.of(configuration.payload.paramsToSet);
    }

    if (patientId) {
      configuration.payload.patientId = patientId;
    }

    if (channelName) {
      configuration.payload.channelName = channelName;
    }

    if (Array.isArray(configuration.payload.paramsToSet)) {
      const allpromise = configuration.payload.paramsToSet.map(async (element, index) => {
        set(configuration.payload.paramsToSet[index], element.key, await this._aggregationService.getFieldFromStore(element));
        if (!element.preserveAttributes) {
          delete configuration.payload.paramsToSet[index]['key'];
          delete configuration.payload.paramsToSet[index]['pathToField'];
          if (has(configuration.payload.paramsToSet[index], 'pathToPick')) { delete configuration.payload.paramsToSet[index]['pathToPick']; }
        }
        return element;
      });

      Promise.all(allpromise).then((data) => {
        // configuration.payload.paramsToSet = Object.assign({ }, ...data);

        // In case config params are nested (e.g. subject.birthDate, subject.gender) gatherig all nested params inside the parent object:
        // subject{
        //   gender: "Male",
        //   birthDate: "09 Oct 1967"
        // }
        const parameters = { };
        for (const elem of data) {
          const itemKey: string = Object.keys(elem)[0];
          const itemValue: any = Object.values(elem)[0]; // changed Item Value to any
          if (typeof(elem[itemKey]) === 'string') {
            parameters[itemKey] = itemValue;
          }
          else if (typeof(elem[itemKey]) === 'object') {
            if (!parameters.hasOwnProperty(itemKey)) {
              parameters[itemKey] = { };
            }
            parameters[itemKey][Object.keys(itemValue)[0]] = Object.values(itemValue)[0];
          }
        }
        configuration.payload.paramsToSet = parameters;

        switch (configuration.command) {
          case BUTTON_ACTIONS.OPEN_MODAL:
            this.openPopupModal(configuration.payload);
            //  this.showWarningForExistingWorkflow(this.newTestConfig.payload.existingWorkflowWarningTitle, '');
            break;
          case BUTTON_ACTIONS.CHANGE_STATE:
            configuration.payload.redirectUrl
              ? this.goToState(configuration.payload.redirectUrl, configuration.payload.paramsToSet)
              : this.router.navigate([configuration.payload.state], { queryParams: configuration.payload.paramsToSet });
        }
      });
    }
    else {
      switch (configuration.command) {
        case BUTTON_ACTIONS.OPEN_MODAL:
          this.openPopupModal(configuration.payload);
          break;
        case BUTTON_ACTIONS.CHANGE_STATE:
          configuration.payload.redirectUrl
            ? this.goToState(configuration.payload.redirectUrl, configuration.payload.paramsToSet)
            : this.router.navigate([configuration.payload.state], { queryParams: configuration.payload.paramsToSet });
      }
    }
  }

  openPopupModal(payload) {
    this.newCaseTunnel = this.popupDynamicFormModal(
      payload.title || '',
      payload.formId,
      payload.paramsToSet, // formData
      payload.approveBtn || 'Confirm',
      payload.declineBtn || 'Cancel',
      payload.channelName,
      payload.class
    ).pipe(
      filter((data: any) => data.status === modalResponse.approve && data.payload.isValid),
      map(data => data)
    );

    this.newCaseTunnel.subscribe(data => {
      if ((data.payload.responseType === 1)) {
        if (payload.autoSaveOnSubmit) {
          return;
        }

        this._store.dispatch({
          type: CORE_ACTIONS.SET_LOADER,
          payload: true
        });

        this.getParentTestId(get(this.newTestConfig, 'payload.testType') || get(data, 'payload.formData.testType'),
                              get(this.newTestConfig, 'payload.useTestTypeAsWorkflowJobsTemplate', false))
          .subscribe((testId: string) => {
            /** If the test ID is not found the override will not happen, and it will use the same test type provided in the payload */
            if (testId) {
              set(data, 'payload.formData.workflowRequest', testId);
            }

            if (this.newTestConfig.payload.showWarningForPatientIfExistingWorkflowExists) {
              this.checkExistingWorkflowForPatient(this.newTestConfig.payload.testType, data.payload.formData);
              this.logService.info(`Inside dashboard service , postNewCasePayload method with payload ${JSON.stringify(data.payload.formData)} `, this.DASHBOARD_SERVICE, this.transactionId);
            }
            else if (this.newTestConfig.payload.skipDiagnosticOrder !== true) {
              this.postNewCasePayload(data.payload.formData);
            }
            else {
              const navigateTo = get(this.newTestConfig.payload, 'redirectUrl', '') || get(this.dashboardConfig, 'redirectUrl', '');
              if (payload.patientId) {
                data.payload.formData.patientId = payload.patientId; // Support opening add test dialog for a specific patient
              }
              this.goToState(navigateTo, { 'rowData': data.payload.formData});
              this._store.dispatch({
                type: CORE_ACTIONS.SET_LOADER,
                payload: false
              });
            }
          });
      }
    });
  }

  popupDynamicFormModal(title: string, formId: string, formData: any, approveBtn?: string, declineBtn?: string, channelName?: string, classes?: string) {
    let channel: any = this.dashboardConfig ? ChannelTypes.NewCaseChannel : ChannelTypes.AllItemsChannel;
    this.channelName = channel = channelName ? channelName : channel;

    return this._formHandler.popupDynamicFormModal(title, formId, formData, approveBtn, declineBtn,
      { 'classes': classes ? classes + ' ' : ''} ,
      channel, null, false);
  }

  closeDynamicFormModal() {
    if (this.channelName) {
      this._formHandler.closeDynamicFormModal(this.channelName);
    }
  }

  /**
   * Pass payload of modal to diagnostic service to create new test
   * data - data from suitable popup,
   * additionalData - additional headers for request
   */
  postNewCasePayload(data: any) {
    if (data.hasOwnProperty('subject')) {
      const newOrder = {
        diagnosticOrder: new DiagnosticOrder(data.subject.id, this.authService.user.role, this.authService)
          .setOrganization(this.authService.user.organization)
          .setManagingOrganization(this.authService.user.managingOrganization)
          .setPerformer(this.authService.user.info.id)
          .setAdditionalData(Object.assign({ discipline: { code: '1', display: 'Medical Oncology'},
            orderer: '',
            priority: 'routine',
            recipients: [],
            reason: [],
            specimen: [],
            identifiers: []
          }, omit(data, 'subject', 'condition', 'text', 'id'))),
        validateSpecimen: false,
        disableOrderValidation: false,
        returnAllOrders: true
      };
      const diagnosticOrderPayload = { ...newOrder};

      // Added to save mdtTypeGroup data in expected model
      if ((<any> newOrder.diagnosticOrder).mdtTypeDecisionTree) {
        diagnosticOrderPayload.diagnosticOrder = { ...newOrder.diagnosticOrder, ...(<any> newOrder.diagnosticOrder).mdtTypeDecisionTree};
        delete (<any> diagnosticOrderPayload.diagnosticOrder).mdtTypeDecisionTree;
      }
      // Remove unneeded keys from practitioner
      diagnosticOrderPayload['practitioner'] = omit(this.authService.user, 'permissions', 'username', 'exp', 'hsdpId', 'hsdpOrg', 'xsrf', 'uiCustomResources', 'localSettings', 'loginTime', 'info.meta');
      // newOrder['asyncWorkflowStart'] = true;
      this.logService.info(`Inside dashboard service , creating diagnostic order with payload ${JSON.stringify(diagnosticOrderPayload)} `, this.DASHBOARD_SERVICE, this.transactionId);
      if ((diagnosticOrderPayload.diagnosticOrder && diagnosticOrderPayload.diagnosticOrder['subject'] == null || diagnosticOrderPayload.diagnosticOrder['subject'] === undefined)) {
        diagnosticOrderPayload.diagnosticOrder['subject'] = this.stateParams && this.stateParams.patientId;
      }
      this._isgHttpClient.post('diagnosticOrder', diagnosticOrderPayload).subscribe(resdata => {
        this.notificationMessageService.success(this.notificationMessage.CREATED_NEW_TEST_SUCCESSFULLY);
        const navigationParams = {
          data: {
            subject: resdata.body.subject,
            patient_id: resdata.body.subject,
            workflowrequest_id: resdata.body.workflowRequest.id,
            revision_id: resdata.body.workflowRequest.activeRevision,
            WorkflowRequest: {
              id: resdata.body.workflowRequest.id,
              'revisions_-1_id': resdata.body.workflowRequest.activeRevision
            }
          }
        };

        this._store.dispatch({ type: CURRENT_ORDER_ACTIONS.SET_IS_NEW_ORDER, payload: true});
        this._store.dispatch({ type: CURRENT_ORDER_ACTIONS.SET_REVISION_ID, payload: resdata.body.workflowRequest.activeRevision});
        this._store.dispatch({ type: CURRENT_ORDER_ACTIONS.SET_TEST_ID, payload: resdata.body.workflowRequest.id});
        this._store.dispatch({ type: CURRENT_ORDER_ACTIONS.SET_PATIENT_ID, payload: resdata.body.subject});
        this._store.dispatch({ type: CURRENT_ORDER_ACTIONS.SET_SKIP_NOTES_FETCH, payload: true});
        if (resdata.body.additionalData) {
          // this._store.dispatch({type: CURRENT_ORDER_ACTIONS.SET_PATIENT, payload: order.patient});
          this._store.dispatch({ type: CURRENT_ORDER_ACTIONS.SET_ALL_ORDERS, payload: resdata.body.additionalData.patientAnalysis});
          this._store.dispatch({ type: CURRENT_ORDER_ACTIONS.SET_CURRENT_ORDER, payload: resdata.body.additionalData.patientAnalysisById});
          this._store.dispatch({ type: CURRENT_ORDER_ACTIONS.SET_ORDERS_COUNT, payload: resdata.body.additionalData.ordersCount});
          this._store.dispatch({ type: CURRENT_ORDER_ACTIONS.SET_WORKFLOW_DATA, payload: resdata.body.additionalData.workflowRequest});
          delete resdata.body.additionalData;
        }
        // Dashboard Config Redirect URL was used for worklist, New Test Config was used in the Test Configuration
        this.goToState(get(this.dashboardConfig, 'redirectUrl', '') || get(this.newTestConfig, 'redirectUrl', ''),
          { 'rowData': navigationParams}, resdata.body.workflowRequestTemplateId);
        this._store.dispatch({ type: DIAGNOSTIC_ORDER_ACTIONS.SET_ORDERS_FOR_TEST_ALL_ITEMS, payload: []});
        this._store.dispatch({
          type: CORE_ACTIONS.SET_LOADER,
          payload: false
        });
      }, error => {
        console.log(error);
        this._store.dispatch({
          type: CORE_ACTIONS.SET_LOADER,
          payload: false
        });
        this.notificationMessageService.error(this.notificationMessage.FAILURE_TO_CREATE);
      });
    } else {
      this._store.dispatch({
        type: CORE_ACTIONS.SET_LOADER,
        payload: false
      });
    }
  }

  /**
   * @method getParentTestId
   * @param testType parent test type
   * @param useTestTypeAsWorkflowJobsTemplate
   * @description fetches the child test id using parent id and organization
   * @returns Observable<string>
   */
  private getParentTestId(testType: string, useTestTypeAsWorkflowJobsTemplate: boolean = false): Observable<string> {
    return this._aggregationService
      .getStoreItemObservable('users.organizationConfiguration.clientGenericIntegrationMap.tests')
      .pipe(
        take(1),
        map(data => get(data, 'tests.' + testType, null)),
        map(parentTestId => !!parentTestId ? parentTestId : (useTestTypeAsWorkflowJobsTemplate ? testType : null)),
        switchMap(neededTestId => (!neededTestId && this.getCurrentTestName() || observableOf(neededTestId))),
        switchMap(neededTestId => {
          return this._diagnosticOrderService.fetchTestIdByContext(this.authService.user.organization, neededTestId).pipe(
            take(1),
            catchError(() => observableOf(null)));
        })
      );
  }

  public goToState(redirectUrl: string, param = { }, waitForStepTestId: string = null) {
    const url = this.interpolateService.interpolate(redirectUrl, param);
    const uriState = this.routerManagerService.getURL(url);

    let params;
    if (redirectUrl && redirectUrl.split(/[?#]/)[1]?.includes("EMRIntegration")) {
      let queryParams = this.routerManagerService.queryParameters;
      params = {...queryParams};
    } else {
      params = this.routerManagerService.getQueryParams(url) ? this.routerManagerService.getQueryParams(url) : {appname: ''};
    }
    const currentApp = sessionStorage.getItem('currentApplication');
    if (!!currentApp) {
      const appName = this.getCurrentApplicationSessionConfiguration('alias');
      params.appname = appName;
    } else {
      delete params.appname
    }

    if (waitForStepTestId) {
      if (param && param['rowData'] && param['rowData'].data) {
        this.testService.getTestConfiguration(waitForStepTestId).then(() => {
          if (this.testService.testConfiguration) {
            const skipLocationChange = uriState && uriState.indexOf(WorklistHelper.PATHOLOGIST_ROUTE) > -1;
            this.pollJobService.pollingUntilStepAndNavigate(param['rowData'].data.revision_id, param['rowData'].data.workflowrequest_id,
              this.testService.testConfiguration, uriState, params, {
                skipLocationChange: skipLocationChange
              });
          } else {
            console.error('goToState: testConfig was not found');
          }
        });
      } else {
        console.error('goToState: missing parameters');
      }
    } else {
      // Not required to reload for admin worklist, ex: patient, practioner.
      this.router.navigate([uriState], {queryParams: params});
    }
  }

  public checkExistingWorkflowForPatient(testType, patientID) {
    const dataBindings: Array<any> = [];
    const dataBindings2: Array<any> = [];

    if (testType) {
      dataBindings.push({
        'queryString': `testType=${testType}&subject=${patientID.subject.id}&_sort:desc=dateOfTest&_fieldslist=workflowRequest`,
        'targetResource': 'DiagnosticOrder'
      });

      this._bundleCall.searchCall({ queries: dataBindings}).subscribe( data => {
        const workflowRequestStatus = [];
        const diagnosticOrderRecords = get(data[0], ['response', 'entry']);
        if (diagnosticOrderRecords.length > 0) {
          diagnosticOrderRecords.forEach((x) => {
            this.workflowId = x.resource.workflowRequest;
            dataBindings2.push({
              'identifier': 'workflowStatus',
              'queryString': `_id=${this.workflowId}`,
              'targetResource': 'WorkflowRequest'
            });
          });
          this._aggregationService.registerQuery(dataBindings2).subscribe(data3 => {
            data3.forEach((datax) => {
              const individualWorkflowRequest = get(datax, ['workflowStatus', 'latestRevisionStatus']);
              workflowRequestStatus.push(individualWorkflowRequest);
            });
            if (workflowRequestStatus.length === data3.length) {
              if (!(workflowRequestStatus.includes('Stopped') || workflowRequestStatus.includes('Completed'))) {
                this.showWarningForExistingWorkflow(this.newTestConfig.payload.existingWorkflowWarningTitle, patientID);
              } else {
                this.postNewCasePayload(patientID);
              }
            }
          });

        } else {
          this.postNewCasePayload(patientID);
        }
      });
    }
  }

  private showWarningForExistingWorkflow(dialogTitle, patientData) {
    this._store.dispatch({
      type: CORE_ACTIONS.SET_LOADER,
      payload: false
    });

    let modalSubscriber$ = this._dynamicModalLoader.popupModal(
        ConfirmModalComponent,
        new ConfirmModalLoaderPayload(
          'Warning',
          dialogTitle,
          'Confirm',
          'Cancel'
        ).setBackdropAllowed(false),
        ModalTypes.ALERT,
        'ShowWarningChannel'
      )
      .subscribe(
        (response) => {
          if (response.payload === 1) {
            this._store.dispatch({
              type: CORE_ACTIONS.SET_LOADER,
              payload: true
            });
            modalSubscriber$.unsubscribe();
            modalSubscriber$ = null;
            this.postNewCasePayload(patientData);
          }
        },
        () => {
        },
        () => {
          modalSubscriber$.unsubscribe();
          modalSubscriber$ = null;
        }
      );
  }

  public loadAllItems() {
    this._store.dispatch({
      type: TABLELIST_ACTIONS.LOAD_ORDERS_FOR_TEST_ALL_ITEMS
    });
  }

  public getOrganizationDataFromStore(): Observable<any> {
    return this.store$.select('organizationConfiguration');
  }

  getFacilityTableConfig(tableConfig: TableItemInterface[], worklistTemplateid: string, worklistTemplateExtends: string): Observable<TableItemInterface[]> {
    return this._aggregationService.isFacilityActiveWithoutAssociation().pipe(map((isFacilityActive) => {
    if (!(isFacilityActive)) {
      return tableConfig;
    }
    const facilityWorklistFilters = get(this.currentApp, 'facilityWorklistFilters', { });
    if (!(facilityWorklistFilters)) {
      return tableConfig;
    }
    const worklisTemplates: Array<{ id: string, pos: number}> = get(facilityWorklistFilters, 'WorklistTemplates', []);
    if (!(worklisTemplates)) {
      return tableConfig;
    }
    const currentTemplate = worklisTemplates.find(function (template) {
      return (template.id === worklistTemplateid || template.id === worklistTemplateExtends);
    });
    if (isEmpty(currentTemplate)) {
      return tableConfig;
    }
    const keyName = get(facilityWorklistFilters, 'tableKey');
    if (!(this.isKeyExists(tableConfig, keyName))) {
      if (facilityWorklistFilters) {
        const managingFacilityTableKey: TableItemInterface = {
          'keys': [
            {
              'key': keyName,
              'type': 'text',
              'value': { },
              'resource': ''
            }
          ],
          'label': get(facilityWorklistFilters, 'label'),
          'width': 'large',
          'sortable': true,
          'icon': null,
          'className': get(facilityWorklistFilters, 'className', ''),
          'tooltip': facilityWorklistFilters.tooltip,
          'tooltipPosition': facilityWorklistFilters.tooltipPosition
        };
        tableConfig.splice(currentTemplate.pos, 0, managingFacilityTableKey);
        return tableConfig;
      } else {
        return tableConfig;
      }
    }
    else {
      return tableConfig;
    }
    }));
  }

  getFacilityAssociationItem(resource: string): any {
    const facilityWorklistFilters = get(this.currentApp, 'facilityWorklistFilters', { });

    if (facilityWorklistFilters) {
      return [
        {
          'name': get(facilityWorklistFilters, 'name'),
          'path': get(facilityWorklistFilters, 'path'),
          'type': get(facilityWorklistFilters, 'type'),
          'operator': '=',
          'resource': resource,
          'tableKey': get(facilityWorklistFilters, 'tableKey')
        },
        {
          'name': 'associatedFacility', // hardcoding as it is hard coded in filter panel also
          'path': get(facilityWorklistFilters, 'associatedFacilityPath'),
          'type': get(facilityWorklistFilters, 'type'),
          'operator': '=',
          'resource': resource
        }
      ];
    }
    else {
      return [];
    }
  }

  addFacilityToFieldsList(resource: string, dySearch: any): any {
    const facilityWorklistFilters = get(this.currentApp, 'facilityWorklistFilters', { });

    if (facilityWorklistFilters) {
    const facilityKey =  get(facilityWorklistFilters, 'tableKey');
      const path =  get(facilityWorklistFilters, 'path');
    if (facilityKey) {
      let fieldsList =  get(dySearch, 'target._fieldslist', '');
      if (fieldsList) {
        fieldsList = facilityKey + ',' + fieldsList;
        if (path) {
          fieldsList = path + ',' + fieldsList;
        }
        set(dySearch, 'target._fieldslist', fieldsList);
      }
      }
     }

    return dySearch;
  }

  isKeyExists(tableConfig, keyName: string): boolean {
    const keys = lodashMap(tableConfig, function (value, key) {
      return lodashMap(value['keys'], 'key');
    });
    const keyArrays = [].concat.apply([], keys);

    return includes(keyArrays, keyName);
   }

  public getSelectedFacilityStringAndAssociation(): Observable<ISelectedFacilityAssociation> {
    return this._aggregationService.getSelectedFacilityStringAndAssociation();
  }

  public isFacilityEnabled(): Observable<boolean> {
    return this._aggregationService.isFacilityActive();
  }

  public isFacilityEnabledWithoutAssociation(): Observable<boolean> {
    return this._aggregationService.isFacilityActiveWithoutAssociation();
  }

  public isInoEnabledWithoutAssociation(): Observable<boolean> {
    return this._aggregationService.isInoActiveWithoutAssociation();
  }

  public getFacilityFilters(managedByFacility: boolean = true, resource: string): Array<Filter> {
    let  filterToApply: Filter;
    const facilityWorklistFilters = get(this.currentApp, 'facilityWorklistFilters', { });

    if (!isEmpty(facilityWorklistFilters)) {

    if (managedByFacility) {
      filterToApply = new Filter(resource, '=', '$(users.selectedFacilitiesString)', get(facilityWorklistFilters, 'associatedFacilityPath'), get(facilityWorklistFilters, 'type'));
    }
    else {
      filterToApply = new Filter(resource, '=', '$(users.selectedFacilitiesString)', get(facilityWorklistFilters, 'path'), get(facilityWorklistFilters, 'type'));
    }
      return [filterToApply];
    } else {
      return [];
    }
  }

  public getCurrentWorkflowTemplateName(): Observable<string> {
    let currentWorkflowTemplateName = '';
    this._store.select('configuration', 'definitions').subscribe(data => {
      try {
        const values: any[] = data.get('Test').entries().next().value;
        values.forEach(value => {
          if (has(value, 'resourceType') && get(value, 'resourceType') === 'Test' && has(value, 'workflowJobsTemplates')) {
            currentWorkflowTemplateName = get(value, 'workflowJobsTemplates', []).find(item => item.usage === 'test').reference;
          }
        });
        return Observable.of(currentWorkflowTemplateName);
      } catch (e) { }
    });

    return Observable.of(currentWorkflowTemplateName);
  }

  public getCurrentTestName(): Observable<string> {
    let currentTestName = '';
    this._store.select('configuration', 'definitions').subscribe(data => {
      try {
        const values: any[] = data.get('Test').entries().next().value;
        values.forEach(value => {
          if (has(value, 'resourceType') && get(value, 'resourceType') === 'Test') {
            currentTestName = get(value, 'id');
          }
        });
        return Observable.of(currentTestName);
      } catch (e) { }
    });

    return Observable.of(currentTestName);
  }

  public getNavigationItems() {
    if (!this.authService.user) {
      return [];
    }

    switch (this.authService.user.role) {
      case 'GenomicsAdminGroup': {
        return [
          {
            itemTitle: 'ISPM',
            itemIcon: 'flaticon-home125',
            itemViewPath: 'dashboard.admin.home'
          },
          {
            itemTitle: 'Organizations',
            itemIcon: 'flaticon-hospital11',
            itemViewPath: 'dashboard.admin.organizations'
          },
          {
            itemTitle: 'Practitioners',
            itemIcon: 'flaticon-family25',
            itemViewPath: 'dashboard.admin.practitioners',
          },
          {
            itemTitle: 'Patients',
            itemIcon: 'flaticon-family25',
            itemViewPath: 'dashboard.admin.patients',
          },
          {
            itemTitle: 'Permission Schemas',
            itemIcon: 'flaticon-family25',
            itemViewPath: 'dashboard.admin.permissionschemas',
          },
          {
            itemTitle: 'Roles',
            itemIcon: 'flaticon-family25',
            itemViewPath: 'dashboard.admin.roles',
          },
          {
            itemTitle: 'Audit',
            itemIcon: 'flaticon-family25',
            itemViewPath: 'dashboard.admin.audit',
          },
          {
            itemTitle: 'Documentation',
            itemIcon: 'flaticon-family25',
            itemViewPath: 'dashboard.admin.configurations'
          }
        ];
      }
      case 'Admin': {
        return [
          {
            itemTitle: 'Practitioners',
            itemIcon: 'flaticon-family25',
            itemViewPath: 'dashboard.admin.practitioners',
            path: '/practitioners'
          },
          {
            itemTitle: 'Patients',
            itemIcon: 'flaticon-family25',
            itemViewPath: 'dashboard.admin.patients',
            path: '/patients'
          }];
      }
      default:
      {
        return [];
      }
    }
  }

  public getClinicalUsageMessage(): any {
    this.nonClinicalUsage = sessionStorage.getItem('nonClinicalUsage');

    if (!this.nonClinicalUsage) {
      return this._isgHttpClient.get('/clinicalUsageLabel').pipe(take(1), map(res => res.body), map(clinicalMessage => {
        sessionStorage.setItem('nonClinicalUsage', JSON.stringify(clinicalMessage));
      })).toPromise();
    } else {
      try {
        this.nonClinicalUsage = JSON.parse(this.nonClinicalUsage);
      } catch (err) {
        this.nonClinicalUsage = { };
      }

      return new Promise((resolve, reject) => {
        resolve(this.nonClinicalUsage);
      });
    }
  }

  public isWorklistsSearchPropagationAvailable() {
    let worklistsSearchPropagation = this.getCurrentApplicationSessionConfiguration('worklistsSearchPropagation');
    return typeof (worklistsSearchPropagation) === 'boolean' ? worklistsSearchPropagation : true;
  }

  private getCurrentApplicationSessionConfiguration(confKey){
    const currentApp = sessionStorage.getItem('currentApplication');
    if (currentApp) {
      const app = JSON.parse(currentApp);
      return get(app, confKey, '');
    }
    return null;
  }
}

export enum ChannelTypes {
  NewCaseChannel = 'NewCaseChannel',
  AllItemsChannel = 'AllItemsChannel'
}
